import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from './communs';
import { nomComplet, simplifier } from '../utils/format';

/**
 * Zone de saisie avec mentions : taper « @ » propose les profils actifs ;
 * ↑ ↓ pour choisir, Entrée ou Tab pour insérer, Échap pour fermer.
 * Ctrl+Entrée (⌘+Entrée sur Mac) envoie le message.
 * onEnvoyer(texte, idsMentionnes) — seules les mentions encore présentes
 * dans le texte au moment de l'envoi sont transmises.
 */
export default function ChampMention({ utilisateurs, onEnvoyer, envoi }) {
  const [texte, setTexte] = useState('');
  const [mentions, setMentions] = useState([]);   // profils insérés via la liste
  const [recherche, setRecherche] = useState(null); // { debut, terme } quand la liste est ouverte
  const [actif, setActif] = useState(0);
  const zone = useRef(null);
  const curseurApres = useRef(null); // position du curseur à poser après une insertion

  // Replacé de façon synchrone, avant que la frappe suivante n'arrive.
  useLayoutEffect(() => {
    if (curseurApres.current === null || !zone.current) return;
    zone.current.focus();
    zone.current.setSelectionRange(curseurApres.current, curseurApres.current);
    curseurApres.current = null;
  }, [texte]);

  const propositions = useMemo(() => {
    if (!recherche) return [];
    const t = simplifier(recherche.terme);
    return utilisateurs
      .filter((u) => {
        const complet = simplifier(nomComplet(u));
        return !t || complet.startsWith(t) || simplifier(u.nom).startsWith(t) || complet.includes(t);
      })
      .slice(0, 8);
  }, [recherche, utilisateurs]);

  function analyser(valeur, curseur) {
    // Le mot en cours commence-t-il par @ (en début de texte ou après un espace) ?
    const avant = valeur.slice(0, curseur);
    const m = avant.match(/(^|\s)@([\p{L}'-]*(?: [\p{L}'-]*)?)$/u);
    if (m) {
      setRecherche({ debut: curseur - m[2].length - 1, terme: m[2] });
      setActif(0);
    } else {
      setRecherche(null);
    }
  }

  function inserer(u) {
    const curseur = zone.current.selectionStart;
    const etiquette = `@${nomComplet(u)} `;
    const nouveau = texte.slice(0, recherche.debut) + etiquette + texte.slice(curseur);
    curseurApres.current = recherche.debut + etiquette.length;
    setTexte(nouveau);
    setMentions((m) => (m.some((x) => x.id === u.id) ? m : [...m, u]));
    setRecherche(null);
  }

  async function envoyer() {
    const propre = texte.trim();
    if (!propre || envoi) return;
    const ids = mentions.filter((u) => propre.includes(`@${nomComplet(u)}`)).map((u) => u.id);
    const ok = await onEnvoyer(propre, ids);
    if (ok !== false) { setTexte(''); setMentions([]); }
  }

  function clavier(e) {
    if (recherche && propositions.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActif((a) => (a + 1) % propositions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActif((a) => (a - 1 + propositions.length) % propositions.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); inserer(propositions[actif]); return; }
    }
    if (e.key === 'Escape' && recherche) { e.stopPropagation(); setRecherche(null); return; }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); envoyer(); }
  }

  return (
    <div className="composeur">
      {recherche && propositions.length > 0 && (
        <ul className="suggestions" role="listbox" aria-label="Mentionner">
          {propositions.map((u, i) => (
            <li
              key={u.id}
              role="option"
              aria-selected={i === actif}
              className={i === actif ? 'actif' : ''}
              onMouseDown={(e) => { e.preventDefault(); inserer(u); }}
              onMouseEnter={() => setActif(i)}
            >
              <Avatar prenom={u.prenom} nom={u.nom} />{nomComplet(u)}
            </li>
          ))}
        </ul>
      )}
      <label htmlFor="composeur-texte" className="sr-only" style={{ position: 'absolute', left: -9999 }}>Votre message</label>
      <textarea
        id="composeur-texte"
        ref={zone}
        value={texte}
        placeholder="Écrire un message… tapez @ pour mentionner quelqu’un"
        onChange={(e) => { setTexte(e.target.value); analyser(e.target.value, e.target.selectionStart); }}
        onKeyDown={clavier}
        onClick={(e) => analyser(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setRecherche(null), 150)}
      />
      <div className="composeur-pied">
        <span><kbd>@</kbd> mentionner · <kbd>Ctrl</kbd>+<kbd>Entrée</kbd> envoyer</span>
        <button type="button" className="btn btn-primaire" onClick={envoyer} disabled={!texte.trim() || envoi}>
          {envoi ? 'Envoi…' : 'Envoyer'}
        </button>
      </div>
    </div>
  );
}
